import { createPortal } from "react-dom";
import { hatcheryDropdownModes } from "@/app/theme";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  MultiSelectDropdown,
  MediaFullscreenButton,
  MediaOverlay,
  MediaTimelineBar,
  Scrollbar,
  SyncedVideoGrid,
  useSyncedVideoTimeline,
  VideoPlaybackBar,
} from "@carbon6/c6-frontend-design";
import type {
  MediaTimelineBarLabels,
  MediaTimelineTrack,
  PlaybackTransport,
  VideoChannelSource,
  VideoPlaybackBarLabels,
} from "@carbon6/c6-frontend-design";
import {
  hasModifierKey,
  isEditableKeyboardTarget,
  isHotkeySurfaceActive,
} from "@/components/keyboard";
import { createPlayerTimelineViewport, type PlayerTimelineViewport } from "./playerViewport";
import { useVideoGridFit, videoGridFrameHeight } from "./useVideoGridFit";
import type { VideoGridFit } from "./videoGridFit";
import styles from "./MultiChannelVideoPlayer.module.css";

// 한 채널(카메라)의 재생 소스. c6 Video 의 채널 계약을 그대로 다시 export 한다. 호출자는
// @/components/video 에서만 import 한다(episodeChannels 등). 재생 엔진은 c6 가 소유한다.
export type { VideoChannelSource };

// 재생 위치 아래에 쌓는 타임라인 행(출처 구간 등). c6 행 계약을 그대로 다시 export 한다.
// 행의 뜻은 호출자가 정한다. 좌표·줌·seek 는 c6 가 소유한다.
export type PlayerTimelineTrack = MediaTimelineTrack;

// 재생 위치만 있는 기본 행이다. 구간 행이 없을 때 바가 이 행 하나를 그린다. 구간 행 없이 다른 행만
// 올리는 쪽도 이 행을 맨 위에 둔다. 그래서 바의 첫 줄이 에피소드마다 같다.
export const PLAYBACK_TIMELINE_ROW: PlayerTimelineTrack = { id: "playback", type: "default" };

// 재생 바 컨트롤의 한국어 라벨(aria-label 겸 툴팁). c6 기본값은 영문이다. 그래서 앱 규약에 맞춰
// 덮어쓴다. 재생/일시정지 라벨에는 단축키를 적는다. c6 버튼 안에는 키캡을 넣을 수 없어서 라벨에
// 적는다. buffering 은 버퍼 대기 상태 문구다. 버퍼 대기가 0.3초 넘게 계속되면 c6 가 알린다. 한 줄
// 배치(`inline`)에서는 Spinner 만 화면에 표시하고 문구는 스크린 리더에만 알린다.
const PLAYBACK_LABELS: VideoPlaybackBarLabels = {
  toStart: "처음으로",
  stepBackward: "뒤로 한 칸",
  play: "재생 (Space)",
  pause: "일시정지 (Space)",
  stop: "정지",
  stepForward: "앞으로 한 칸",
  toEnd: "끝으로",
  seek: "재생 위치",
  buffering: "버퍼링 중…",
};

// 행을 쌓는 타임라인의 라벨. 재생 컨트롤은 위와 같은 문구를 쓰고, 줌·창 이동 라벨을 추가한다.
// c6 는 창 이동 버튼을 "timeline segment" 라 부른다. 그러나 이 버튼은 확대한 창의 앞뒤로
// 이동한다. 그래서 "구간"이라 쓰지 않는다. 이 화면에서 구간은 action_source 구간이다.
const TIMELINE_LABELS: MediaTimelineBarLabels = {
  ...PLAYBACK_LABELS,
  toMarker: "표시로 이동",
  markers: "표시",
  timelineZoom: "타임라인 확대 비율",
  zoomOut: "축소",
  resetZoom: "확대 초기화",
  zoomIn: "확대",
  playbackRate: "재생 속도",
  previousTimelineSegment: "앞쪽 보기",
  nextTimelineSegment: "뒤쪽 보기",
};

// 크게 보기 버튼의 한국어 라벨. c6 기본값이 영문이다. 그래서 앱 규약에 맞춰 덮어쓴다.
const EXPAND_LABEL = "크게 보기";
const COLLAPSE_LABEL = "크게 보기 닫기";

/**
 * 플레이어를 놓는 자리. 이 값이 재생 바를 화면 안에 유지하는 방법을 정한다.
 *
 * - `primary`: 넓은 자리. 격자와 바가 함께 스크롤하고, 스크롤은 바깥이 소유한다.
 * - `compact`: 좁은 세로 띠(데이터셋 리뷰 탭의 표본 레인). 카메라가 넷이면 한 열로 쌓여 격자가
 *   길어진다. 바깥과 함께 스크롤하면 재생·seek 컨트롤이 화면 밖으로 나간다. 그래서 격자만 안에서
 *   스크롤하고 바는 아래에 붙인다.
 */
export type MultiChannelVideoPlayerMode = "primary" | "compact";

type MultiChannelVideoPlayerProps = {
  channels: readonly VideoChannelSource[];
  mode?: MultiChannelVideoPlayerMode;
  channelVisibility?: boolean;
  // 재생 바만 다른 자리로 옮긴다(에피소드 미리보기의 pane 하단 블록). 타임라인 수명은 플레이어가
  // 유지한다.
  //   - 생략하면 격자 아래에 그린다.
  //   - `null` 이면 옮길 자리가 아직 없다. 그래서 그리지 않는다.
  // 제자리에 그렸다가 자리가 생길 때 옮기면 바가 다시 마운트되어 확대 상태를 잃는다.
  transportContainer?: HTMLElement | null;
  // URL 만료 판단과 재발급은 파일 목록을 소유한 소비처가 담당한다.
  onChannelError?: (channelId: string) => void;
  // 채널이 없을 때(대표 영상을 모를 때 등) 표시할 안내.
  emptyMessage?: string;
  // 채널별 프레임 높이 cap(CSS length). c6 grid 계약과 같다. 생략하면 컨테이너 폭을 채운다.
  // `fit` 이 있으면(compact) `fit` 을 우선한다.
  maxHeight?: string;
  // compact 에서 격자 상자를 재서 모든 카메라가 들어가는 프레임 높이를 고른다. 하한보다 작아야
  // 하면 격자가 스크롤한다. 숨긴 카메라는 계산에서 제외한다. 규칙 객체는 안정 참조여야 한다.
  // 참조가 바뀔 때마다 다시 잰다.
  fit?: VideoGridFit;
  // 맞춘 프레임 높이(px)가 바뀌면 호출한다. 격자 바깥이 같은 값을 따라야 하는 자리(영상 열 폭)가 쓴다.
  onFrameFit?: (px: number) => void;
  // 카메라를 자세히 보는 두 방법을 함께 켠다.
  //   - 프레임 안에서 Ctrl+휠로 확대·이동한다(c6 ZoomPan).
  //   - [크게 보기]로 한 대만 화면 가득 표시한다(오버레이).
  // 격자에서는 타일이 작아서 제자리 확대만으로는 부족하다. 오버레이만 있으면 잠깐 볼 때도
  // 열고 닫는 동작이 필요하다. 두 방법은 한 기능이다. 그래서 스위치도 하나다.
  //
  // 기본은 꺼짐이다. 단일 채널만 표시하는 자리(파일 탭)에서는 이미 그 카메라만 보인다.
  zoomable?: boolean;
  // 재생 바의 "한 칸"(앞으로/뒤로) 이 이동하는 초. 카탈로그가 이 영상의 fps 를 가지면
  // 1/fps 를 전달해 한 칸을 한 프레임으로 맞춘다. 생략하면 c6 기본값(0.1s)을 쓴다. 그러면
  // 프레임과 관계없는 고정 폭으로 이동한다. fps 를 모르는 자리에서는 이 동작이 맞다.
  stepSeconds?: number;
  // 값을 전달하면 재생 바 대신 행을 쌓는 타임라인을 쓴다. 재생 위치 아래에 구간 행을 함께 둔다.
  // 생략하면 가벼운 재생 바를 쓴다. 행 배열은 안정 참조여야 한다(c6 가 배열 식별자로 캐시한다).
  timelineTracks?: readonly PlayerTimelineTrack[];
  // 행 타임라인이 창을 보관할 store. 같은 시간축을 옆에서 그리는 소비처(진단 운동 차트)가 이
  // store 를 구독한다. 그래서 소비처가 자기 창을 따로 가지지 않는다. **창의 소유자는 항상 c6
  // 바다.** 소비처는 읽기만 한다. 창을 옮기려면 그 시각으로 seek 한다. 가벼운 재생 바(행 없음)와
  // 채널 0개에는 바가 붙지 않는다. 그때 store 의 스냅샷은 null 이다. 생략하면 이 컴포넌트가
  // 같은 기준(에피소드 전체 길이)의 store 를 만든다.
  timelineViewport?: PlayerTimelineViewport;
  // 밖에서 재생 위치를 옮길 수 있게 transport 를 알린다(진단 그래프 → 영상 시간축).
  // 타임라인은 이 컴포넌트가 계속 소유한다. 소비처는 명령만 보낸다. 언마운트·재생성 때는
  // null 을 알린다. 그래서 소비처가 오래된 핸들을 보관하지 않는다.
  onTransportReady?: (transport: PlaybackTransport | null) => void;
};

// 여러 카메라 채널을 한 타임라인으로 동기 재생하는 플레이어.
//
// 동기 재생·drift 보정·채널 종료 blackout 은 c6 Video 의 `useSyncedVideoTimeline` 엔진이
// 소유한다. 이 컴포넌트는 버퍼 대기(`waitForBuffer`)를 켠다. 카메라 네 대(각 2–3 Mbps)는 서명
// URL 로 GCS 에서 직접 받는다. 느린 망에서 한 대가 뒤처지면 엔진이 시계를 멈춘다. 모든 카메라가
// 2초 앞까지 받으면 함께 다시 재생한다. 버퍼 대기를 끄면 엔진이 뒤처진 카메라를 아직 받지 못한
// 위치로 계속 seek 한다. 그러면 진행 중인 요청이 끊긴다.
//
// 그리드(`SyncedVideoGrid`)와 재생 bar(`VideoPlaybackBar`, 행을 쌓으면 `MediaTimelineBar`)는
// 같은 transport 핸들 하나를 공유한다. 그리드에는 timeline 으로, bar 에는 transport 로 전달한다.
// 재생 바는 이 컴포넌트에서만 그린다(lint 가 강제한다). Space 정책이 이 파일 한 곳에 있기 때문이다.
// 이 컴포넌트는 둘을 묶고, 채널이 바뀌면 처음으로 되돌리는 앱 경계만 담당한다. 채널 URL 을
// 구하는 방법(어느 episode, 서명 URL 등)은 호출자가 channels 로 주입한다.
export function MultiChannelVideoPlayer({
  channels,
  onChannelError,
  mode = "primary",
  channelVisibility = false,
  transportContainer,
  emptyMessage,
  maxHeight,
  fit,
  onFrameFit,
  zoomable = false,
  stepSeconds,
  timelineTracks,
  timelineViewport,
  onTransportReady,
}: MultiChannelVideoPlayerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const timeline = useSyncedVideoTimeline({ channels, waitForBuffer: true });
  const channelIdsKey = JSON.stringify(channels.map((channel) => channel.id));
  const [hiddenChannels, setHiddenChannels] = useState<readonly string[]>([]);
  const visibleChannels = useMemo(
    () =>
      channelVisibility
        ? channels.filter((channel) => !hiddenChannels.includes(channel.id))
        : channels,
    [channels, channelVisibility, hiddenChannels],
  );
  const visibleChannelIds = useMemo(
    () => visibleChannels.map((channel) => channel.id),
    [visibleChannels],
  );
  // compact 의 격자 상자. fit 이 이 상자를 잰다. 상자가 다시 마운트되면 다시 재야 한다.
  // 그래서 ref 가 아니라 요소를 state 로 보관한다.
  const [gridScroll, setGridScroll] = useState<HTMLDivElement | null>(null);
  const gridFit = mode === "compact" ? fit : undefined;
  useVideoGridFit(gridScroll, { fit: gridFit, count: visibleChannels.length, onFrameFit });
  const channelOptions = useMemo(
    () =>
      channels.map((channel) => ({
        value: channel.id,
        label: typeof channel.label === "string" ? channel.label : channel.id,
      })),
    [channels],
  );
  // 확대해 보는 채널. 열림 상태는 consumer 가 소유한다(c6 는 셸과 버튼만 제공한다).
  const [expandedChannelId, setExpandedChannelId] = useState<string | null>(null);
  const expandedChannel = channels.find((channel) => channel.id === expandedChannelId) ?? null;
  const expandedChannels = useMemo(
    () => (expandedChannel === null ? [] : [expandedChannel]),
    [expandedChannel],
  );

  // 콘텐츠 구성이 바뀌면 확대를 닫는다. 같은 채널의 URL 갱신은 보던 화면을 유지한다.
  useEffect(() => {
    setExpandedChannelId(null);
    setHiddenChannels([]);
  }, [channelIdsKey]);

  useEffect(() => {
    onTransportReady?.(timeline);
    return () => onTransportReady?.(null);
  }, [onTransportReady, timeline]);

  // 소비처가 창 store 를 주지 않아도 행 타임라인은 에피소드 전체 길이로 연다.
  const [ownTimelineViewport] = useState(createPlayerTimelineViewport);

  // 채널 ID 목록이 바뀌면(에피소드·데이터셋 전환) 처음으로 되돌린다. URL 갱신만으로는
  // 재생을 멈추지 않는다. 그래서 배열 참조 대신 ID와 순서로 콘텐츠 전환을 판단한다.
  useEffect(() => {
    timeline.reset();
  }, [channelIdsKey, timeline]);

  // 최신 transport·재생 가능 여부를 effect 재구독 없이 참조한다.
  const playbackRef = useRef({ timeline, hasChannels: channels.length > 0, transportContainer });
  playbackRef.current = { timeline, hasChannels: channels.length > 0, transportContainer };

  /**
   * `Space` → 재생/일시정지.
   *
   * 사용자가 영상을 보면서 목록을 훑으려면 포커스와 관계없이 동작해야 한다. 그래서 window 에서
   * 키를 받는다. 다음 네 경우에는 처리하지 않는다.
   * - 이미 처리된 키.
   * - 수식키 조합.
   * - 입력창. 사용자가 공백을 입력한다.
   * - **플레이어 자신의 컨트롤.** 재생 버튼에 포커스가 있으면 native 활성화가 이미 토글한다.
   *   여기서 다시 토글하면 원래 상태로 돌아간다.
   * 보이지 않는 표면(배경 라우트 페이지·창 위 모달)은 inert 가드가 제외한다.
   *
   * 그 결과 이 화면의 **다른** 버튼들은 Space 로 활성화되지 않는다(Enter 는 그대로다). 목록 행을
   * 클릭한 직후에도 Space 로 재생하는 동작이 이 화면의 흐름에 맞다. 그래서 이 방식을 골랐다.
   *
   * 누르고 있을 때의 반복 입력은 토글하지 않는다. 행 타임라인(c6 `MediaTimelineBar`)의 규칙과
   * 같다. 그 타임라인이 같은 window 에서 Space 를 먼저 처리하면 `defaultPrevented` 때문에 여기서는
   * 처리하지 않는다. 바깥 버튼에 포커스가 있으면 c6 가 처리하지 않고 여기서 처리한다. 이 경우와
   * 가벼운 재생 바를 쓰는 경우에 반복 입력을 거르지 않으면, 키를 누르는 동안 재생이 깜빡인다.
   */
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key !== " " || event.repeat || event.defaultPrevented || hasModifierKey(event)) {
        return;
      }
      const {
        timeline: transport,
        hasChannels,
        transportContainer: controls,
      } = playbackRef.current;
      if (!hasChannels) return;
      if (isEditableKeyboardTarget(event.target)) return;
      const root = rootRef.current;
      if (!isHotkeySurfaceActive(root)) return;
      if (
        event.target instanceof Node &&
        (root?.contains(event.target) || controls?.contains(event.target))
      )
        return;
      // 공백은 페이지를 스크롤한다. Space 를 재생에 쓰는 경우에는 그 기본 동작을 막는다.
      event.preventDefault();
      transport.toggle();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const grid = (
    <SyncedVideoGrid
      // 숨긴 타일은 unmount하여 attachment/decoder를 해제한다. 전체 channels를 쓰는
      // timeline은 그대로 둔다. 그래서 모두 숨겨도 길이·현재 시점이 바뀌지 않는다.
      channels={visibleChannels}
      timeline={timeline}
      onChannelError={(channel) => onChannelError?.(channel.id)}
      emptyMessage={emptyMessage ?? "표시할 대표 영상이 없습니다."}
      maxHeight={gridFit === undefined ? maxHeight : videoGridFrameHeight(gridFit)}
      // 높이를 제한하는 자리에서는 프레임을 가운데에 둔다. 폭은 aspect 로 정해져 칸보다 좁아진다.
      // 왼쪽에 붙이면 오른쪽만 비어서 격자가 어긋나 보인다.
      frameAlign={mode === "compact" ? "center" : undefined}
      // 켜면 c6 가 프레임 액션을 ZoomPan 의 액션 행으로 옮긴다. 그래서 확대 컨트롤과 한 줄에 정렬된다.
      zoomable={zoomable}
      renderFrameActions={
        zoomable
          ? (channel) => (
              <MediaFullscreenButton
                enterLabel={EXPAND_LABEL}
                onClick={() => setExpandedChannelId(channel.id)}
              />
            )
          : undefined
      }
    />
  );

  const playbackBar =
    channels.length === 0 ? null : timelineTracks === undefined ? (
      <VideoPlaybackBar
        transport={timeline}
        labels={PLAYBACK_LABELS}
        stepSeconds={stepSeconds}
        // 표본 레인에서는 seek 트랙을 별도 줄에 둬 이동 버튼·시간 표기와 겹치지 않게 한다.
        layout={mode === "compact" ? "stacked" : "inline"}
      />
    ) : (
      <MediaTimelineBar
        transport={timeline}
        tracks={timelineTracks}
        labels={TIMELINE_LABELS}
        stepSeconds={stepSeconds}
        viewport={timelineViewport ?? ownTimelineViewport}
      />
    );

  return (
    <div ref={rootRef} className={styles.player} data-mode={mode}>
      {channelVisibility && channels.length > 0 ? (
        <MultiSelectDropdown
          label="영상 채널"
          mode={hatcheryDropdownModes.filter}
          size="sm"
          options={channelOptions}
          selectedValues={visibleChannelIds}
          onSelectionChange={(values) =>
            setHiddenChannels(
              channels
                .filter((channel) => !values.includes(channel.id))
                .map((channel) => channel.id),
            )
          }
          closeOnSelect={false}
          clearSelectionLabel="모두 숨기기"
          selectAllLabel="모두 보기"
          menuWidth="20rem"
        />
      ) : null}
      {/* compact 만 격자에 스크롤 경계를 준다. 넓은 자리에서 격자 높이를 제한하면 아래가 잘린
          것처럼 보인다. 그 자리는 바깥이 이미 스크롤한다. */}
      {mode === "compact" ? (
        <Scrollbar ref={setGridScroll} className={styles.gridScroll} gutter="auto">
          {channelVisibility && channels.length > 0 && visibleChannelIds.length === 0 ? (
            <p className={styles.hiddenMessage}>
              모든 영상이 숨겨져 있습니다. 영상 채널에서 다시 켤 수 있습니다.
            </p>
          ) : null}
          {grid}
        </Scrollbar>
      ) : (
        grid
      )}
      {transportContainer === undefined
        ? playbackBar
        : transportContainer === null
          ? null
          : createPortal(playbackBar, transportContainer)}

      {/* 확대는 같은 timeline 을 공유한다. 새로 만들면 재생 위치가 달라진다. 그러면 크게 본 뒤
          닫았을 때 영상이 다른 위치에 있다. 재생 bar 는 뒤에 그대로 있다. 그래서 오버레이에
          다시 두지 않는다. */}
      <MediaOverlay
        open={expandedChannel !== null}
        onClose={() => setExpandedChannelId(null)}
        // 채널 라벨은 ReactNode 라서 이름으로 그대로 쓸 수 없다. 카메라 이름은 캡션이 이미
        // 표시한다. 그래서 여기서는 이 표면이 무엇인지만 알린다.
        aria-label={EXPAND_LABEL}
      >
        {expandedChannel === null ? null : (
          <div className={styles.expanded}>
            <SyncedVideoGrid
              channels={expandedChannels}
              timeline={timeline}
              onChannelError={(channel) => onChannelError?.(channel.id)}
              aspectRatioMode="channel"
              frameAlign="center"
              maxHeight="calc(100dvh - 4.5rem)"
              zoomable
              renderFrameActions={() => (
                <MediaFullscreenButton
                  fullscreenActive
                  exitLabel={COLLAPSE_LABEL}
                  onClick={() => setExpandedChannelId(null)}
                />
              )}
            />
          </div>
        )}
      </MediaOverlay>
    </div>
  );
}
